/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import DescriptionIcon from "@material-ui/icons/Description";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import RepeatIcon from "@material-ui/icons/Repeat";
import StopIcon from "@material-ui/icons/Stop";
import ScheduleIcon from "@material-ui/icons/Schedule";

import PageBase from "../../components/PageBase";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Grid, FormControl, InputLabel, Select, MenuItem, TablePagination, Pagination, Box } from "@material-ui/core";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import { formatCompanyDateTime, resolveCompanyTimezone } from "../../utils/companyTimezone";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];

    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const campaignIndex = state.findIndex((u) => u.id === campaign.id);
        if (campaignIndex !== -1) {
          state[campaignIndex] = campaign;
        } else {
          newCampaigns.push(campaign);
        }
      });
    }

    return [...state, ...newCampaigns];
  }

  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaign.id);

    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    } else {
      return [campaign, ...state];
    }
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;

    const campaignIndex = state.findIndex((u) => u.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.padding,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  recurringChip: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: '0.75rem',
  },
  statusChip: {
    fontSize: '0.75rem',
  },
  nextExecutionCell: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  filterContainer: {
    marginBottom: theme.spacing(2),
  },
  tableHeader: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.grey[100],
  },
}));

const Campaigns = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaigns, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    campaigns.forEach((campaign) => {
      if (campaign?.scheduledAt) {
        console.log("[FRONT-CAMPAIGN-RENDER]", campaign.scheduledAt);
        console.log("[TZ-FRONT]", {
          userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          apiResponse: campaign.scheduledAt,
          rendered: datetimeToClient(campaign.scheduledAt)
        });
      }
    });
  }, [campaigns]);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [stopRecurrenceModalOpen, setStopRecurrenceModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [recurrenceFilter, setRecurrenceFilter] = useState("");
  const { user, socket } = useContext(AuthContext);
  const companyTimezone = resolveCompanyTimezone(
    user?.company?.timezone || localStorage.getItem("companyTimezone")
  );

  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useCampaigns) {
        toast.error(i18n.t("campaigns.toasts.noPermissionRedirect"));
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, statusFilter, recurrenceFilter, pageSize]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCampaigns();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, statusFilter, recurrenceFilter, pageSize]);

  useEffect(() => {
    const companyId = user.companyId;

    const onCompanyCampaign = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
      }
    }

    socket.on(`company-${companyId}-campaign`, onCompanyCampaign);
    return () => {
      socket.off(`company-${companyId}-campaign`, onCompanyCampaign);
    };
  }, [user]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns/", {
        params: { 
          searchParam, 
          pageNumber, 
          pageSize,
          status: statusFilter,
          isRecurring: recurrenceFilter 
        },
      });
      
      // Para paginação real, substituir os dados em vez de adicionar
      if (pageNumber === 1) {
        dispatch({ type: "RESET" });
      }
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records });
      
      setHasMore(data.hasMore);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.count || 0);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleRecurrenceFilterChange = (event) => {
    setRecurrenceFilter(event.target.value);
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleStopRecurrence = async (campaignId) => {
    try {
      await api.post(`/campaigns/${campaignId}/stop-recurrence`);
      toast.success(i18n.t("campaigns.toasts.stopRecurrence"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    }
    setStopRecurrenceModalOpen(false);
    setSelectedCampaign(null);
  };

  const handlePageChange = (event, newPage) => {
    setPageNumber(newPage + 1); // Material-UI usa índice baseado em 0
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPageNumber(1);
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return i18n.t("campaigns.status.inactive");
      case "PROGRAMADA":
        return i18n.t("campaigns.status.scheduled");
      case "EM_ANDAMENTO":
        return i18n.t("campaigns.status.inProgress");
      case "SUSPENSA_CONEXAO":
        return i18n.t("campaigns.status.suspendedConnection");
      case "CANCELADA":
        return i18n.t("campaigns.status.cancelled");
      case "FINALIZADA":
        return i18n.t("campaigns.status.finished");
      default:
        return val;
    }
  };

  const getCampaignOperationalStatus = (campaign) => {
    if (campaign.status === "INATIVA") return i18n.t("campaigns.operationalStatus.paused");
    if (campaign.status === "PROGRAMADA") return i18n.t("campaigns.operationalStatus.scheduled");
    if (campaign.status === "CANCELADA") return i18n.t("campaigns.operationalStatus.cancelled");
    if (campaign.status === "FALHOU") return i18n.t("campaigns.operationalStatus.failed");
    if (campaign.status === "SUSPENSA_CONEXAO") return i18n.t("campaigns.operationalStatus.suspendedManual");
    if (campaign.status === "FINALIZADA") return i18n.t("campaigns.operationalStatus.completed");
    if (campaign.status === "EM_ANDAMENTO") return i18n.t("campaigns.operationalStatus.running");
    if (campaign.completedAt) return i18n.t("campaigns.operationalStatus.completed");
    if (campaign.startedAt) return i18n.t("campaigns.operationalStatus.running");
    return i18n.t("campaigns.operationalStatus.pending");
  };

  const canEditCampaign = (campaign) => {
    const blockedStatuses = ["EM_ANDAMENTO", "SUSPENSA_CONEXAO", "FINALIZADA", "CANCELADA", "FALHOU"];
    const canEdit = !campaign.startedAt && !campaign.completedAt && !blockedStatuses.includes(campaign.status);
    console.log("[CAMPAIGN_CAN_EDIT]", {
      id: campaign.id,
      status: campaign.status,
      scheduledAt: campaign.scheduledAt,
      startedAt: campaign.startedAt,
      completedAt: campaign.completedAt,
      canEdit
    });
    return canEdit;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "INATIVA":
        return "default";
      case "PROGRAMADA":
        return "primary";
      case "EM_ANDAMENTO":
        return "secondary";
      case "SUSPENSA_CONEXAO":
        return "secondary";
      case "CANCELADA":
        return "default";
      case "FINALIZADA":
        return "default";
      default:
        return "default";
    }
  };

  const formatRecurrenceType = (type) => {
    switch (type) {
      case "minutely":
        return i18n.t("campaigns.recurrence.minutely");
      case "hourly":
        return i18n.t("campaigns.recurrence.hourly");
      case "daily":
        return i18n.t("campaigns.recurrence.daily");
      case "weekly":
        return i18n.t("campaigns.recurrence.weekly");
      case "biweekly":
        return i18n.t("campaigns.recurrence.biweekly");
      case "monthly":
        return i18n.t("campaigns.recurrence.monthly");
      case "yearly":
        return i18n.t("campaigns.recurrence.yearly");
      default:
        return type;
    }
  };

  const pauseCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/pause`);
      toast.success(i18n.t("campaigns.toasts.pause"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <PageBase
      className="campaigns-page app-page-root"
      title={i18n.t("campaigns.title")}
      actions={
        !(user.profile === "user" && user?.showCampaign === "disabled") && (
          <div className="campaigns-actions">
            <TextField
              className="search-input"
              placeholder={i18n.t("campaigns.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={handleOpenCampaignModal}
              color="primary"
            >
              {i18n.t("campaigns.buttons.add")}
            </Button>
          </div>
        )
      }
      toolbar={
        !(user.profile === "user" && user?.showCampaign === "disabled") && (
          <div className="campaigns-toolbar">
            <div className="campaigns-filters">
              <FormControl variant="outlined" size="small">
                <InputLabel>{i18n.t("campaigns.filters.status")}</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  label={i18n.t("campaigns.filters.status")}
                >
                  <MenuItem value="">{i18n.t("campaigns.filters.all")}</MenuItem>
                  <MenuItem value="INATIVA">{i18n.t("campaigns.status.inactive")}</MenuItem>
                  <MenuItem value="PROGRAMADA">{i18n.t("campaigns.status.scheduled")}</MenuItem>
                  <MenuItem value="EM_ANDAMENTO">{i18n.t("campaigns.status.inProgress")}</MenuItem>
                  <MenuItem value="SUSPENSA_CONEXAO">{i18n.t("campaigns.status.suspendedConnection")}</MenuItem>
                  <MenuItem value="CANCELADA">{i18n.t("campaigns.status.cancelled")}</MenuItem>
                  <MenuItem value="FINALIZADA">{i18n.t("campaigns.status.finished")}</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small">
                <InputLabel>{i18n.t("campaigns.filters.recurrence")}</InputLabel>
                <Select
                  value={recurrenceFilter}
                  onChange={handleRecurrenceFilterChange}
                  label={i18n.t("campaigns.filters.recurrence")}
                >
                  <MenuItem value="">{i18n.t("campaigns.filters.allFemale")}</MenuItem>
                  <MenuItem value="true">{i18n.t("campaigns.filters.recurring")}</MenuItem>
                  <MenuItem value="false">{i18n.t("campaigns.filters.single")}</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>
        )
      }
      containerProps={{
        className: classes.mainPaper,
      }}
    >
      <ConfirmationModal
        title={
          deletingCampaign &&
          `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${deletingCampaign.name}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <ConfirmationModal
        title={i18n.t("campaigns.confirmationModal.stopRecurrenceTitle")}
        open={stopRecurrenceModalOpen}
        onClose={() => setStopRecurrenceModalOpen(false)}
        onConfirm={() => handleStopRecurrence(selectedCampaign?.id)}
      >
        {i18n.t("campaigns.confirmationModal.stopRecurrenceMessage")}
      </ConfirmationModal>

      {campaignModalOpen && (
        <CampaignModal
          resetPagination={() => {
            setPageNumber(1);
            fetchCampaigns();
          }}
          open={campaignModalOpen}
          onClose={handleCloseCampaignModal}
          aria-labelledby="form-dialog-title"
          campaignId={selectedCampaign && selectedCampaign.id}
        />
      )}
      {user.profile === "user" && user?.showCampaign === "disabled" ? (
        <ForbiddenPage />
      ) : (
      <div className="page-table-wrapper app-table-zebra">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center" className={classes.tableHeader}>
                  {i18n.t("campaigns.table.name")}
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  {i18n.t("campaigns.table.status")}
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  {i18n.t("campaigns.table.recurrence")}
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  {i18n.t("campaigns.table.contactList")}
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  {i18n.t("campaigns.table.whatsapp")}
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  {i18n.t("campaigns.table.scheduledAt")}
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  {i18n.t("campaigns.table.nextExecution")}
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  {i18n.t("campaigns.table.completedAt")}
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  {i18n.t("campaigns.table.confirmation")}
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  {i18n.t("campaigns.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <>
                {campaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell align="center">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        {campaign.isRecurring && (
                          <Tooltip title={i18n.t("campaigns.tooltips.recurringCampaign")}>
                            <RepeatIcon color="primary" fontSize="small" />
                          </Tooltip>
                        )}
                        {campaign.name}
                      </div>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={
                          campaign.status === "SUSPENSA_CONEXAO"
                            ? i18n.t("campaigns.tooltips.suspendedConnection")
                            : ""
                        }
                      >
                        <Chip
                          label={getCampaignOperationalStatus(campaign)}
                          color={getStatusColor(campaign.status)}
                          size="small"
                          className={classes.statusChip}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      {campaign.isRecurring ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                          <Chip
                            label={formatRecurrenceType(campaign.recurrenceType)}
                            className={classes.recurringChip}
                            size="small"
                          />
                          <span style={{ fontSize: '0.75rem', color: '#666' }}>
                            {i18n.t("campaigns.recurrence.executions", { count: campaign.executionCount || 0 })}
                          </span>
                        </div>
                      ) : (
                        <Chip
                          label={i18n.t("campaigns.recurrence.single")}
                          variant="outlined"
                          size="small"
                          className={classes.statusChip}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {campaign.contactListId
                        ? campaign.contactList?.name || i18n.t("campaigns.fallbacks.removedList")
                        : campaign.tagListId && campaign.tagListId !== "Nenhuma"
                        ? `Tag: ${campaign.tagListId}`
                        : i18n.t("campaigns.fallbacks.notDefined")}
                    </TableCell>
                    <TableCell align="center">
                      {campaign.whatsappId
                        ? campaign.whatsapp?.name || i18n.t("campaigns.fallbacks.removedWhatsapp")
                        : i18n.t("campaigns.fallbacks.notDefined")}
                    </TableCell>
                    <TableCell align="center">
                      {campaign.scheduledAt
                        ? formatCompanyDateTime(campaign.scheduledAt, campaign.timezone || companyTimezone)
                        : i18n.t("campaigns.fallbacks.noSchedule")}
                    </TableCell>
                    <TableCell align="center" className={classes.nextExecutionCell}>
                      {campaign.isRecurring && campaign.nextScheduledAt ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                          <ScheduleIcon fontSize="small" color="primary" />
                          {formatCompanyDateTime(campaign.nextScheduledAt, campaign.timezone || companyTimezone)}
                        </div>
                      ) : campaign.isRecurring && campaign.status === 'FINALIZADA' ? (
                        <span style={{ color: '#666', fontSize: '0.875rem' }}>
                          {i18n.t("campaigns.recurrence.finished")}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {campaign.completedAt
                        ? formatCompanyDateTime(campaign.completedAt, campaign.timezone || companyTimezone)
                        : i18n.t("campaigns.fallbacks.notCompleted")}
                    </TableCell>
                    <TableCell align="center">
                      {campaign.confirmation ? i18n.t("common.status.enabled") : i18n.t("common.status.disabled")}
                    </TableCell>
                    <TableCell align="center">
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
                        {campaign.status === "EM_ANDAMENTO" && (
                          <Tooltip title={i18n.t("campaigns.tooltips.pauseCampaign")}>
                            <IconButton
                              onClick={() => pauseCampaign(campaign)}
                              size="small"
                            >
                              <PauseCircleOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(campaign.status === "CANCELADA" || campaign.status === "INATIVA" || campaign.status === "SUSPENSA_CONEXAO") && (
                          <Tooltip title={campaign.status === "SUSPENSA_CONEXAO" ? i18n.t("campaigns.tooltips.resumeCampaignManually") : i18n.t("campaigns.tooltips.resumeCampaign")}>
                            <IconButton
                              onClick={() => restartCampaign(campaign)}
                              size="small"
                            >
                              <PlayCircleOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {campaign.isRecurring && campaign.status !== "FINALIZADA" && (
                          <Tooltip title={i18n.t("campaigns.tooltips.stopRecurrence")}>
                            <IconButton
                              onClick={() => {
                                setSelectedCampaign(campaign);
                                setStopRecurrenceModalOpen(true);
                              }}
                              size="small"
                              color="secondary"
                            >
                              <StopIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={i18n.t("campaigns.tooltips.report")}>
                          <IconButton
                            onClick={() =>
                              history.push(`/campaign/${campaign.id}/report`)
                            }
                            size="small"
                          >
                            <DescriptionIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={i18n.t("common.buttons.edit")}>
                          <IconButton
                            size="small"
                            disabled={!canEditCampaign(campaign)}
                            onClick={() => handleEditCampaign(campaign)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={i18n.t("common.buttons.delete")}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setConfirmModalOpen(true);
                              setDeletingCampaign(campaign);
                            }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={10} />}
              </>
            </TableBody>
          </Table>
          
          {/* Paginação */}
          <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box color="text.secondary" fontSize="0.875rem">
                {i18n.t("campaigns.pagination.total", { count: totalCount })}
              </Box>
            </Box>
            
            <TablePagination
              component="div"
              count={totalCount}
              page={pageNumber - 1} // Material-UI usa índice baseado em 0
              onPageChange={handlePageChange}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handlePageSizeChange}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              labelRowsPerPage={i18n.t("campaigns.pagination.rowsPerPage")}
              labelDisplayedRows={({ from, to, count }) => 
                i18n.t("campaigns.pagination.displayedRows", { from, to, count: count !== -1 ? count : i18n.t("campaigns.pagination.moreThan", { to }) })
              }
            />
          </Box>
        </div>
      )}
    </PageBase>
  );
};

export default Campaigns;
